import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Activity, CheckCircle2, Circle, Folder, Percent, ArrowRight,
  AlertTriangle, Search, Sparkles, Scale, DollarSign, Leaf, Users, Landmark, Gauge,
  BookOpen, BookMarked, FileEdit, PlayCircle, Loader2,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell,
} from "recharts";

const TYPE_META = {
  juridique: { label: "Juridique", short: "JUR", icon: Scale, color: "#D4A017" },
  financier: { label: "Financière", short: "FIN", icon: DollarSign, color: "#1B4332" },
  environnemental: { label: "Environnement (SEC)", short: "SEC", icon: Leaf, color: "#27AE60" },
  social: { label: "Social (SSC)", short: "SSC", icon: Users, color: "#1A3C5E" },
  souverainete: { label: "Souveraineté (SOS)", short: "SOS", icon: Landmark, color: "#8B4513" },
  desequilibre: { label: "Déséquilibre (IDC)", short: "IDC", icon: Gauge, color: "#E67E22" },
  diagnostic: { label: "Diagnostic", short: "DIA", icon: AlertTriangle, color: "#C0392B" },
  bln_confrontation: { label: "BLN", short: "BLN", icon: BookOpen, color: "#6B7280" },
  jurisprudence_argument: { label: "Jurisprudence", short: "JUR+", icon: BookMarked, color: "#8B4513" },
  amendment_rewrite: { label: "Amendements", short: "AMD", icon: FileEdit, color: "#D4A017" },
};

const CORE_TYPES = ["juridique", "financier", "environnemental", "social", "souverainete"];

const SCORE_COLOR = (s) =>
  s >= 70 ? "#27AE60" : s >= 50 ? "#D4A017" : s >= 30 ? "#E67E22" : "#C0392B";

export default function Analyses() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | analyzed | unanalyzed | full
  const [search, setSearch] = useState("");
  const [batchRunning, setBatchRunning] = useState(false);

  const reload = () =>
    api.get("/analyses/summary?limit=500").then((r) => setData(r.data));

  useEffect(() => {
    reload().finally(() => setLoading(false));
  }, []);

  const runBatch = async () => {
    if (batchRunning) return;
    setBatchRunning(true);
    try {
      const res = await api.post("/analyses/batch-run");
      const r = res.data || {};
      const parts = [];
      if (r.processed > 0) parts.push(`${r.processed} projet(s) analysé(s)`);
      if (r.analyses_written > 0) parts.push(`${r.analyses_written} analyses écrites`);
      if ((r.skipped_already_done || []).length)
        parts.push(`${r.skipped_already_done.length} déjà complet(s)`);
      if ((r.skipped_no_extract || []).length)
        parts.push(`${r.skipped_no_extract.length} sans document extrait`);
      if ((r.failed || []).length)
        parts.push(`${r.failed.length} échec(s)`);
      toast.success(parts.join(" · ") || "Aucun projet à traiter.");
      await reload();
    } catch (e) {
      toast.error("Erreur lors du batch d'analyses.");
    } finally {
      setBatchRunning(false);
    }
  };

  const filteredProjects = useMemo(() => {
    if (!data) return [];
    const q = search.toLowerCase().trim();
    return (data.projects || []).filter((p) => {
      if (filter === "analyzed" && p.core_done_count === 0) return false;
      if (filter === "unanalyzed" && p.core_done_count > 0) return false;
      if (filter === "full" && p.core_done_count < 5) return false;
      if (q) {
        const blob = `${p.name} ${p.country} ${p.sector} ${p.resource_type || ""}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [data, filter, search]);

  const barData = useMemo(() => {
    if (!data) return [];
    return CORE_TYPES.map((t) => ({
      type: TYPE_META[t].short,
      label: TYPE_META[t].label,
      avg: data.averages?.[t] || 0,
      color: TYPE_META[t].color,
    }));
  }, [data]);

  if (loading || !data) {
    return (
      <div className="p-6 text-sm opacity-60" data-testid="analyses-page-loading">
        Chargement des analyses…
      </div>
    );
  }

  const t = data.totals || {};
  const hasProjects = (t.projects || 0) > 0;

  return (
    <div className="p-6 space-y-6" data-testid="analyses-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
            <Activity size={28} style={{ color: "#D4A017" }} />
            Analyses
          </h1>
          <p className="text-sm opacity-70 mt-1">
            Vue consolidée du statut d'analyse et des scores moyens sur l'ensemble de vos projets.
          </p>
        </div>
        <button
          onClick={runBatch}
          disabled={batchRunning || !hasProjects}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-semibold transition-all"
          style={{
            background: batchRunning ? "transparent" : "#D4A017",
            color: batchRunning ? "#D4A017" : "#111",
            border: "2px solid #D4A017",
            cursor: batchRunning || !hasProjects ? "not-allowed" : "pointer",
            opacity: !hasProjects ? 0.5 : 1,
          }}
          data-testid="an-batch-run"
          title="Lance les 5 analyses core (financier, environnemental, social, déséquilibre, souveraineté) sur tous les projets où elles manquent. Sans appel LLM — rapide et gratuit."
        >
          {batchRunning ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Traitement en cours…
            </>
          ) : (
            <>
              <PlayCircle size={14} />
              Lancer les analyses manquantes
            </>
          )}
        </button>
      </div>

      {/* KPIs */}
      <div className="grid md:grid-cols-4 gap-3">
        <Stat label="Projets" value={t.projects} icon={Folder} color="#1A3C5E" testid="an-kpi-projects" />
        <Stat
          label="Au moins 1 analyse"
          value={t.analyzed_any}
          icon={CheckCircle2}
          color="#27AE60"
          testid="an-kpi-analyzed"
        />
        <Stat
          label="Complet (≥5 core)"
          value={t.fully_analyzed}
          icon={Sparkles}
          color="#D4A017"
          testid="an-kpi-full"
        />
        <Stat
          label="Analyses totales"
          value={t.total_analyses_run}
          icon={Activity}
          color="#8B4513"
          testid="an-kpi-total"
        />
      </div>

      {!hasProjects ? (
        <div className="rap-card p-10 text-center" data-testid="an-empty">
          <Folder size={32} style={{ color: "#D4A017" }} className="mx-auto mb-3" />
          <h3 className="font-display font-bold text-base mb-2">Aucun projet</h3>
          <p className="text-xs opacity-70 max-w-md mx-auto mb-3">
            Créez un projet et uploadez une convention pour commencer.
          </p>
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 text-sm font-semibold"
            style={{ color: "#D4A017" }}
          >
            Mes projets <ArrowRight size={14} />
          </Link>
        </div>
      ) : (
        <>
          {/* Coverage + Averages */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rap-card p-5" data-testid="an-coverage">
              <h2 className="font-display font-bold text-base mb-3 flex items-center gap-2">
                <Percent size={14} style={{ color: "#D4A017" }} />
                Couverture par type d'analyse
              </h2>
              <div className="space-y-2">
                {(data.coverage || []).map((c) => {
                  const meta = TYPE_META[c.type] || {};
                  const Icon = meta.icon || Circle;
                  return (
                    <div
                      key={c.type}
                      className="flex items-center gap-2"
                      data-testid={`an-cov-${c.type}`}
                    >
                      <Icon size={12} style={{ color: meta.color || "#6B7280" }} />
                      <div className="flex-1 text-xs font-mono opacity-80">{c.label}</div>
                      <div
                        className="h-2 rounded-sm"
                        style={{
                          width: `${Math.max(c.pct, 2)}%`,
                          minWidth: 4,
                          background: meta.color || "#6B7280",
                          opacity: 0.9,
                        }}
                      />
                      <div className="w-16 text-right text-xs font-mono">
                        {c.count} · {c.pct}%
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rap-card p-5" data-testid="an-averages-chart">
              <h2 className="font-display font-bold text-base mb-3 flex items-center gap-2">
                <Gauge size={14} style={{ color: "#D4A017" }} />
                Scores moyens (analyses core)
              </h2>
              <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="type" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(v, _, p) => [`${v}/100`, p.payload.label]}
                      labelFormatter={() => ""}
                    />
                    <Bar dataKey="avg" radius={[4, 4, 0, 0]}>
                      {barData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-[10px] opacity-60 mt-2 font-mono">
                Moyenne des scores sur les projets ayant lancé l'analyse correspondante.
              </p>
            </div>
          </div>

          {/* Projects matrix */}
          <div className="rap-card p-5" data-testid="an-projects-list">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="font-display font-bold text-base">
                Projets ({filteredProjects.length}/{(data.projects || []).length})
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <div
                  className="flex items-center gap-1 border rounded-sm px-2 py-1"
                  style={{ borderColor: "hsl(var(--border))" }}
                >
                  <Search size={12} className="opacity-60" />
                  <input
                    placeholder="Rechercher…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="bg-transparent text-xs outline-none w-48"
                    data-testid="an-search"
                  />
                </div>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="text-xs border rounded-sm bg-transparent px-2 py-1"
                  style={{ borderColor: "hsl(var(--border))" }}
                  data-testid="an-filter"
                >
                  <option value="all">Tous projets</option>
                  <option value="analyzed">Au moins 1 analyse</option>
                  <option value="unanalyzed">Aucune analyse</option>
                  <option value="full">Complets (≥5 core)</option>
                </select>
              </div>
            </div>

            {filteredProjects.length === 0 ? (
              <div className="text-xs opacity-60 text-center py-10">
                Aucun projet ne correspond aux filtres actifs.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider opacity-60 font-mono">
                      <th className="py-2 pr-3">Projet</th>
                      {CORE_TYPES.map((t) => (
                        <th key={t} className="py-2 px-2 text-center">
                          {TYPE_META[t].short}
                        </th>
                      ))}
                      <th className="py-2 px-2 text-center">DIA</th>
                      <th className="py-2 pl-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProjects.map((p) => (
                      <tr
                        key={p.id}
                        className="border-t"
                        style={{ borderColor: "hsl(var(--border))" }}
                        data-testid={`an-row-${p.id}`}
                      >
                        <td className="py-2 pr-3">
                          <div className="font-semibold">{p.name}</div>
                          <div className="text-[10px] opacity-60 font-mono">
                            {p.country} · {p.sector} · {p.resource_type}
                          </div>
                        </td>
                        {CORE_TYPES.map((t) => (
                          <StatusCell key={t} cell={p.status_map?.[t]} type={t} testid={`an-cell-${p.id}-${t}`} />
                        ))}
                        <StatusCell cell={p.status_map?.diagnostic} type="diagnostic" testid={`an-cell-${p.id}-diagnostic`} />
                        <td className="py-2 pl-3 text-right">
                          <Link
                            to={`/projects/${p.id}`}
                            className="inline-flex items-center gap-1 text-xs font-semibold"
                            style={{ color: "#D4A017" }}
                            data-testid={`an-open-${p.id}`}
                          >
                            Ouvrir <ArrowRight size={12} />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StatusCell({ cell, type, testid }) {
  const meta = TYPE_META[type] || {};
  const done = cell?.done;
  const score = cell?.score;
  if (!done) {
    return (
      <td className="py-2 px-2 text-center" data-testid={testid}>
        <Circle size={12} className="inline opacity-30" />
      </td>
    );
  }
  if (typeof score === "number") {
    return (
      <td className="py-2 px-2 text-center" data-testid={testid}>
        <span
          className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-sm"
          style={{ background: `${SCORE_COLOR(score)}22`, color: SCORE_COLOR(score) }}
        >
          {score}
        </span>
      </td>
    );
  }
  return (
    <td className="py-2 px-2 text-center" data-testid={testid}>
      <CheckCircle2 size={12} className="inline" style={{ color: meta.color || "#27AE60" }} />
    </td>
  );
}

function Stat({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="rap-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
        <Icon size={14} style={{ color }} />
      </div>
      <div className="font-mono text-2xl font-bold" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
